// src/app/nav-stack.ts
// The top bar's Back (design log 13 Oct, Rule "Back on the routing history, capped at the
// Dashboard"): one small in-app stack of the top-level pages visited in this tab.
//   - opening a top-level page pushes it;
//   - reaching the Dashboard resets the stack (the cap: Back never walks past home);
//   - opening a page already in the stack cuts the stack back to it, so Back never loops between
//     sibling pages;
//   - Back goes to the page before, or to the Dashboard when nothing is left.
// The browser's own Back still walks the full browser history.
import { useEffect, useSyncExternalStore } from 'react';

export const HOME_SECTION = 'dashboard';

let stack: string[] = [];
const subs = new Set<() => void>();
const emit = () => subs.forEach((f) => f());

export function visitSection(section: string): void {
  if (section === HOME_SECTION) stack = [HOME_SECTION];
  else {
    const i = stack.indexOf(section);
    stack = i >= 0 ? stack.slice(0, i + 1) : [...stack, section];
  }
  emit();
}

/** Where Back goes from `current`: the page before it, else the Dashboard. Null on the Dashboard. */
export function backTarget(current: string): string | null {
  if (current === HOME_SECTION) return null;
  const i = stack.lastIndexOf(current);
  return (i > 0 ? stack[i - 1] : null) ?? HOME_SECTION;
}

/** Test seam. */
export function resetNavStackForTests(): void {
  stack = [];
}

/** Records `section` (null = not a top-level page) and returns Back's target. */
export function useNavStack(section: string | null): string | null {
  useEffect(() => {
    if (section) visitSection(section);
  }, [section]);
  useSyncExternalStore(
    (f) => {
      subs.add(f);
      return () => subs.delete(f);
    },
    () => stack,
    () => stack,
  );
  return section ? backTarget(section) : null;
}
